// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import Mousetrap from 'mousetrap';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import utils from '../../utils';
import KeySignature from '../../uTheory.Notation/keySignature';
import Tonicization from '../../uTheory.Notation/tonicization';
import TonicOrNotGameBoard from './TonicOrNotGameBoard.svelte';
import { getSolfegeSystemsMapForScaleMode } from '../getSolfegeSystemsMapForScaleMode';
import type {
  InteractiveOptions,
  LayoutItem,
  LayoutSettings,
  UserData,
} from '../interactivePrototype';
import InteractivePrototype from '../interactivePrototype';
import { Resolution } from '../../uTheory.Notation/resolution';
import type { InteractiveRegistryEntry } from '../../uTheory.Skills/interactiveRegistry';
import type { ScaleDegree } from '../solfegeDefinitions';

interface TonicOrNotOptions extends InteractiveOptions {
  /** Default: 'Major' */
  mode?: ModeName;
}

interface TonicOrNotLayout extends LayoutSettings {
  svelteDiv: LayoutItem;
}

class TonicOrNot extends InteractivePrototype {
  declare currentQuestion: ScaleDegree;

  declare questions: ScaleDegree[];

  declare options: TonicOrNotOptions;

  private key: KeySignature;

  private resolution: Resolution;

  private svelteDiv: HTMLDivElement;

  private gameBoard: TonicOrNotGameBoard;

  private mousetrap: Mousetrap.MousetrapInstance;

  declare layout: TonicOrNotLayout;

  private splashScreenDismissed = false;

  constructor(displayDiv: HTMLDivElement, options: TonicOrNotOptions, userData: UserData) {
    options = {
      drill: true,
      mode: 'Major',
      ...options,
    };

    super(displayDiv, options, userData);
    this.init(displayDiv, options, userData);
  }

  async init(displayDiv: HTMLDivElement, options: TonicOrNotOptions, userData: UserData) {
    this.settings = {
      loadAudioFirst: true,
      instructions: false,
      checkMark: true,
      player: true,
      instructionsPanel: {
        innerHTML:
          "<p>You'll hear a tonicization, and then a note. " +
          ' Is that note tonic, or not tonic?</p>',
        callback: () => {
          this.splashScreenDismissed = true;
          this.displayInstructions();
        },
      },
      progressTracker: true,
    };

    const quality = options.mode === 'Major' ? 'Major' : 'minor';
    this.key = new KeySignature(~~(Math.random() * 12) - 6, quality);
    await super.init(displayDiv, options, userData);
    this.resolution = new Resolution(this.player);
  }

  generateQuestions(questions: any[] = this.options.questions) {
    console.log('generating questions');
    const { scaleDegrees } = getSolfegeSystemsMapForScaleMode(
      this.options.mode || 'Major',
      'solfegeDoMinor'
    );
    this.questions = utils.shuffle(scaleDegrees.map((d) => d.name));
    console.log(questions);
  }

  async displayInstructions() {
    if (!this.splashScreenDismissed) return;
    if (this.questionNumber === 0) {
      const tonicizer = new Tonicization(this.player, this.key);
      await tonicizer.tonicize(this.key, 100);
      console.log('done awaiting!');
      this.gameBoard.$set({ tonicizing: false });

      this.playQuestion();
    } else {
      this.playQuestion();
    }
  }

  playQuestion() {
    this.gameBoard.$set({
      playingAnswer: false,
    });

    const note = this.key.getNoteAtScaleDegreeOrThrow(this.currentQuestion);
    this.player.play(note);
  }

  async playAnswer() {
    const note = this.key.getNoteAtScaleDegreeOrThrow(this.currentQuestion);
    this.gameBoard.$set({
      playingAnswer: true,
    });
    await this.resolution.resolve(this.key, note, { tempo: 100 });
    this.gameBoard.$set({
      playingAnswer: false,
    });
  }

  createCustomDivs() {
    this.svelteDiv = document.createElement('div');
    this.gameBoard = new TonicOrNotGameBoard({
      target: this.svelteDiv,
      props: {
        interactive: this,
      },
    });
    this.displayDiv.appendChild(this.svelteDiv);
    const mt = Mousetrap(this.svelteDiv);
    mt.bind(['t', 'T'], () => this.checkAnswer('tonic'));
    mt.bind(['n', 'N'], () => this.checkAnswer('not'));

    this.mousetrap = mt;

    this.layout.svelteDiv = {
      div: this.svelteDiv,
      visible: true,
      percent: 100,
      callback: this.gameBoard.resize,
    };
  }

  gameOver() {
    this.gameBoard.$set({ gameOver: true });
    this.mousetrap.reset();
    return super.gameOver();
  }

  exit() {
    this.gameBoard.$destroy();
    super.exit();
  }

  checkAnswer(studentAnswer: 'tonic' | 'not') {
    const currentQuestionIsTonic = this.currentQuestion === '1' || this.currentQuestion === '8';
    const correct = currentQuestionIsTonic ? studentAnswer === 'tonic' : studentAnswer === 'not';

    // When correct, call rightAnswer();
    if (correct) {
      this.player.stop();
      return this.rightAnswer(1000 * 4 * (60 / 100), { skills: ['tonic_or_not'] });
    }
    // When incorrect, call wrongAnswer();
    return this.wrongAnswer(undefined, { skills: ['tonic_or_not'] });
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: any) {
    console.error(questionString);
    throw Error('Checkpoint mode not implemented for solfegeScaleDegreeHandSignMultipleChoice');
    return Promise.reject('Checkpoint mode not implemented');
  }

  static registry: InteractiveRegistryEntry = {
    skills: ['tonic_or_not'],
    description: 'Tonic or not?',
  };
}

export default TonicOrNot;
